import { useEffect, useMemo } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/frontend/contexts/auth-context";
import { useAtomValue, useSetAtom } from "jotai";
import {
  currentProfileAtom,
  profileLoadingStatusAtom,
  profileErrorCodeAtom,
  fetchCurrentProfileAtom,
  userRoleAtom,
} from "@lib/atoms";
import type { Profile } from "@/isomorphic/schemas";
import {
  isOnboardingRoute as checkIsOnboardingRoute,
  isLogoutRoute as checkIsLogoutRoute,
  extractLocale,
} from "@/frontend/config/routes";
import { isValidRedirect } from "@/frontend/lib/utils/redirect";
import { hasBetaAccessRole } from "@/isomorphic/utils/system-role-access";

/**
 * Check if profile has required fields
 */
function isProfileComplete(profile: Profile | null): boolean {
  if (!profile) return false;
  // Required fields: firstName, lastName
  return !!(profile.firstName && profile.lastName);
}

/**
 * Hook to guard routes that require a completed profile
 * Redirects to onboarding if profile is missing or incomplete
 */
export function useProfileGuard() {
  const { user, loading: authLoading, getToken, clearSession } = useAuth();
  const profile = useAtomValue(currentProfileAtom);
  const profileLoadingStatus = useAtomValue(profileLoadingStatusAtom);
  const profileErrorCode = useAtomValue(profileErrorCodeAtom);
  const userRole = useAtomValue(userRoleAtom);
  const fetchProfile = useSetAtom(fetchCurrentProfileAtom);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Use useMemo to return boolean values instead of functions
  // This avoids function reference changes that trigger useEffect re-executions

  const isOnboardingRouteValue = useMemo(() => {
    if (!pathname) return false;
    return checkIsOnboardingRoute(pathname);
  }, [pathname]);

  const isLogoutRouteValue = useMemo(() => {
    if (!pathname) return false;
    return checkIsLogoutRoute(pathname);
  }, [pathname]);

  useEffect(() => {
    async function checkAndLoadProfile() {
      // Skip if auth loading, no user, role not loaded yet, or on logout page
      if (authLoading || !user || userRole === null || isLogoutRouteValue) {
        return;
      }

      // Users without beta access are handled by BetaAccessGuard and must not hit profile APIs.
      if (!hasBetaAccessRole(userRole)) {
        return;
      }

      try {
        // Load profile if not loaded yet
        if (profileLoadingStatus === "init") {
          const jwt = await getToken();
          if (jwt) {
            await fetchProfile(jwt);
          }
        }
      } catch (error) {
        console.error("[ProfileGuard] Error loading profile:", error);
        // fetchProfile already sets profileLoadingStatus to "error"
        // No need to handle state here
      }
    }

    checkAndLoadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, userRole, profileLoadingStatus, isLogoutRouteValue]);

  // Clear invalid session ONLY on 401 (invalid JWT), not on authorization or network errors
  useEffect(() => {
    if (profileLoadingStatus === "error" && user && profileErrorCode === 401) {
      clearSession();
    }
  }, [profileLoadingStatus, user, profileErrorCode, clearSession]);

  // Check if we need to redirect to/from onboarding
  useEffect(() => {
    // Skip if auth loading, no user, role not loaded yet, or profile not loaded yet
    if (authLoading || !user || userRole === null || profileLoadingStatus !== "success") return;

    if (!hasBetaAccessRole(userRole)) return;

    const locale = pathname ? extractLocale(pathname) : "fr";

    // Case 1: User with complete profile on /onboarding → redirect to intended destination
    if (isOnboardingRouteValue && isProfileComplete(profile)) {
      const redirect = searchParams.get("redirect");
      const safeRedirect = redirect && isValidRedirect(redirect) ? redirect : "/";
      router.push(`/${locale}${safeRedirect}`);
      return;
    }

    // Case 2: User with incomplete profile on protected route → redirect to onboarding
    if (!isOnboardingRouteValue && !isProfileComplete(profile)) {
      // Use existing redirect param, or capture current path as redirect destination
      const currentRedirect = searchParams.get("redirect");
      const pathWithoutLocale = pathname ? pathname.replace(/^\/[a-z]{2}/, "") : "/";
      const redirectTarget = currentRedirect || pathWithoutLocale || "/";
      const redirectUrl = encodeURIComponent(redirectTarget);
      router.push(`/${locale}/onboarding?redirect=${redirectUrl}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, userRole, profile, profileLoadingStatus, pathname, isOnboardingRouteValue]);

  return {
    isLoading:
      authLoading ||
      (user &&
        userRole !== null &&
        hasBetaAccessRole(userRole) &&
        !isLogoutRouteValue &&
        (profileLoadingStatus === "loading" || profileLoadingStatus === "init")),
    hasError: profileLoadingStatus === "error",
    hasProfile: isProfileComplete(profile),
    profile,
    needsOnboarding:
      !authLoading &&
      user &&
      userRole !== null &&
      hasBetaAccessRole(userRole) &&
      !isProfileComplete(profile) &&
      profileLoadingStatus === "success" &&
      !isOnboardingRouteValue,
  };
}
